import React from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Package, Users, Building, Truck, BarChart2, Globe, HelpCircle, Bell, Shield, Menu, X } from 'lucide-react';
import { jwtDecode } from 'jwt-decode';
import './DashboardLayout.css';

import { useState, useEffect, useRef } from 'react';
import { useNotifications } from '../hooks/useNotifications';
import { useLanguage } from '../contexts/LanguageContext';
import { HelpModal } from '../components/shared/HelpModal';
import { PrivacyPolicyDrawer } from '../components/shared/PrivacyPolicyDrawer';
import { AdminProfileFlyout } from '../components/shared/AdminProfileFlyout';
import { SearchBar } from '../components/shared/SearchBar';
import AddPackageModal from '../components/shared/AddPackageModal/AddPackageModal';
import { globalSearch } from '../services/mock/SearchService';
import cacheStorage from '../utils/cacheStorage';

export default function DashboardLayout() {
  const navigate = useNavigate();
  const [isCouriersOpen, setIsCouriersOpen] = useState(false);
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [isPrivacyOpen, setIsPrivacyOpen] = useState(false);
  const [isAddPackageOpen, setIsAddPackageOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearchDropdownOpen, setIsSearchDropdownOpen] = useState(false);
  const [isSearchFocused, setIsSearchFocused] = useState(false);

  const { language, setLanguage, t } = useLanguage();

  const languageDropdownRef = useRef(null);
  const profileDropdownRef = useRef(null);
  const notificationsDropdownRef = useRef(null);
  const searchDropdownRef = useRef(null);

  const { notifications, unreadCount: unreadNotificationsCount, markAsRead } = useNotifications("admin");

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (languageDropdownRef.current && !languageDropdownRef.current.contains(event.target)) {
        setIsLanguageOpen(false);
      }
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target)) {
        setIsProfileOpen(false);
      }
      if (notificationsDropdownRef.current && !notificationsDropdownRef.current.contains(event.target)) {
        setIsNotificationsOpen(false);
      }
      if (searchDropdownRef.current && !searchDropdownRef.current.contains(event.target)) {
        setIsSearchDropdownOpen(false);
        setIsSearchFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

// Get user details from JWT token
  let userName = cacheStorage.getItem('user_name') || "User";   // CHANGED
  let userInitial = userName.charAt(0).toUpperCase();            // CHANGED
  let userRole = null;
  let userPicture = cacheStorage.getItem('user_picture');

  try {
    const token = cacheStorage.getItem('id_token');
    if (token) {
      const decoded = jwtDecode(token);
      // Only fall back to JWT if cache is empty          // ADD THIS
      if (!cacheStorage.getItem('user_name')) {           // ADD THIS
        userName = decoded.name || decoded.nickname || decoded.email?.split('@')[0] || "User";
        userInitial = userName.charAt(0).toUpperCase();
        cacheStorage.setItem('user_name', userName);      // ADD THIS
      }                                                   // ADD THIS
      userRole = decoded['https://SmartCargo/roles']?.[0] || null;

      if (!userPicture && decoded.picture) {
        userPicture = decoded.picture;
        cacheStorage.setItem('user_picture', decoded.picture);
      }
    }
  } catch (error) {
    console.error("Failed to decode token", error);
  }

// ADD THIS (after the try/catch, before navLinks)
  const userRoleLabel = userRole
      ? userRole.charAt(0).toUpperCase() + userRole.slice(1)
      : "Admin";

  const navLinks = [
    { name: 'Dashboard', path: '/admin', icon: LayoutDashboard },
    { name: 'Packages', path: '/admin/packages', icon: Package },
    {
      name: 'Couriers',
      path: '/admin/couriers',
      icon: Users,
      hasDropdown: true,
      subLinks: [
        { name: 'Couriers List', path: '/admin/couriers' },
        { name: 'Courier Performance', path: '/admin/couriers/performance' }
      ]
    },
    { name: 'Warehouses', path: '/admin/warehouses', icon: Building },
    { name: 'Vehicles', path: '/admin/vehicles', icon: Truck },
    { name: 'Analytics', path: '/admin/analytics', icon: BarChart2 },
  ];

  if (userRole === 'master admin') {
    navLinks.push({ name: 'Admins', path: '/admin/admins', icon: Shield });
  }

  const handleNavClick = (e, link) => {
    if (link.hasDropdown) {
      e.preventDefault();
      setIsCouriersOpen(!isCouriersOpen);
      // We can also navigate if we want, but let's just open the dropdown,
      // or navigate to the main list. Let's navigate to the main list.
      if (!isCouriersOpen) {
        navigate(link.path);
      }
    } else {
      setIsMobileMenuOpen(false);
    }
  };

  useEffect(() => {
    const fetchResults = async () => {
      if (searchQuery.trim().length > 0) {
        const results = await globalSearch(searchQuery, userRole);
        setSearchResults(results);
        setIsSearchDropdownOpen(true);
      } else {
        setSearchResults([]);
        setIsSearchDropdownOpen(false);
      }
    };
    fetchResults();
  }, [searchQuery, userRole]);

  const handleSearchResultClick = (result) => {
    navigate(`${result.path}?search=${encodeURIComponent(result.id)}`);
    setSearchQuery('');
    setIsSearchDropdownOpen(false);
    setIsSearchFocused(false);
  };

  return (
      <div className="dashboard-layout">
        {/* Mobile Overlay */}
        {isMobileMenuOpen && (
            <div
                className="mobile-overlay"
                onClick={() => setIsMobileMenuOpen(false)}
            />
        )}

        {/* Sidebar */}
        <aside className={`admin-sidebar ${isMobileMenuOpen ? 'mobile-open' : ''}`}>
          <div className="sidebar-logo-area">
            <div className="logo-icon">SC</div>
            <div className="logo-text">
              <h2>Smart Cargo</h2>
              <span>Admin Dashboard</span>
            </div>
            {isMobileMenuOpen && (
                <button className="mobile-close-btn" onClick={() => setIsMobileMenuOpen(false)}>
                  <X size={24} />
                </button>
            )}
          </div>

          <nav className="sidebar-nav">
            {navLinks.map((link) => (
                <div key={link.name} className="nav-item-container">
                  <NavLink
                      to={link.path}
                      className={({ isActive }) =>
                          `sidebar-link ${isActive ? 'active' : ''}`
                      }
                      end={link.path === '/admin' || link.path === '/admin/couriers'}
                      onClick={(e) => handleNavClick(e, link)}
                  >
                    <link.icon className="nav-icon" size={20} />
                    <span className="nav-text">{link.name}</span>
                    {link.hasDropdown && (
                        <svg
                            className={`chevron-icon ${isCouriersOpen ? 'open' : ''}`}
                            width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                        >
                          <polyline points="6 9 12 15 18 9"></polyline>
                        </svg>
                    )}
                  </NavLink>
                  {link.hasDropdown && isCouriersOpen && (
                      <div className="sidebar-sublinks">
                        {link.subLinks.map(subLink => (
                            <NavLink
                                key={subLink.name}
                                to={subLink.path}
                                end
                                className={({ isActive }) =>
                                    `sidebar-sublink ${isActive ? 'active' : ''}`
                                }
                                onClick={() => setIsMobileMenuOpen(false)}
                            >
                              {subLink.name}
                            </NavLink>
                        ))}
                      </div>
                  )}
                </div>
            ))}
          </nav>
        </aside>

        {/* Header */}
        <header className="admin-header">
          {/* Mobile Search Overlay */}
          {isSearchFocused && (
              <div
                  className="mobile-search-overlay"
                  onClick={() => setIsSearchFocused(false)}
              />
          )}

          <div className={`header-left ${isSearchFocused ? 'search-focused' : ''}`}>
            <button
                className="mobile-menu-btn"
                onClick={() => setIsMobileMenuOpen(true)}
                aria-label="Open Menu"
            >
              <Menu size={24} />
            </button>
            <div
                className="global-search-container"
                style={{ position: 'relative', display: 'flex', alignItems: 'center', width: '100%' }}
                ref={searchDropdownRef}
                onClickCapture={() => setIsSearchFocused(true)}
            >
              <SearchBar
                  value={searchQuery}
                  onChange={setSearchQuery}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && searchResults.length > 0) {
                      handleSearchResultClick(searchResults[0]);
                    }
                  }}
                  placeholder={t?.searchPlaceholder || "Search..."}
              />
              {isSearchDropdownOpen && searchResults.length > 0 && (
                  <div className="search-results-dropdown">
                    {searchResults.map((result) => (
                        <div key={`${result.type}-${result.id}`} className="search-result-item" onClick={() => handleSearchResultClick(result)}>
                          <div className="search-result-title">{result.title}</div>
                          <div className="search-result-subtitle">{result.type} • {result.subtitle}</div>
                        </div>
                    ))}
                  </div>
              )}
              {isSearchDropdownOpen && searchQuery && searchResults.length === 0 && (
                  <div className="search-results-dropdown">
                    <div className="search-result-empty">No results found</div>
                  </div>
              )}
            </div>
          </div>

          <div className="header-right">
            <div className="notifications-selector" style={{ position: 'relative', cursor: 'pointer', display: 'flex', alignItems: 'center' }} ref={notificationsDropdownRef}>
              <div onClick={() => setIsNotificationsOpen(!isNotificationsOpen)} style={{ position: 'relative' }}>
                <Bell size={20} className="header-icon" />
                {unreadNotificationsCount > 0 && (
                    <span style={{
                      position: 'absolute',
                      top: '-4px',
                      right: '-4px',
                      backgroundColor: '#EF4444',
                      color: 'white',
                      borderRadius: '9999px',
                      width: '16px',
                      height: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '10px',
                      fontWeight: 'bold'
                    }}>
                  {unreadNotificationsCount}
                </span>
                )}
              </div>

              {isNotificationsOpen && (
                  <div className="notifications-popover">
                    <div className="notifications-header">
                      <h3 className="notifications-title">{t.recentNotifications || "Recent Notifications"}</h3>
                    </div>
                    <div className="notifications-list">
                      {notifications.length === 0 ? (
                          <div className="notifications-empty">No notifications</div>
                      ) : (
                          notifications.slice(0, 5).map(notif => (
                              <div
                                  key={notif.id}
                                  className={`notification-item ${notif.is_read ? "read" : "unread"}`}
                                  onClick={() => markAsRead(notif.id)}
                              >
                                <p>{notif.title}</p>
                                <span>{notif.time}</span>
                              </div>
                          ))
                      )}
                    </div>
                    <div className="notifications-footer" onClick={() => { setIsNotificationsOpen(false); navigate("/admin/notifications"); }}>
                      {t.viewAllNotifications || "View All Notifications"}
                    </div>
                  </div>
              )}
            </div>

            <div className="language-selector" style={{ position: 'relative' }} onClick={() => setIsLanguageOpen(!isLanguageOpen)} ref={languageDropdownRef}>
              <Globe size={18} className="header-icon" />
              <span>{language}</span>
              {isLanguageOpen && (
                  <div className="dropdown-menu">
                    {['AZE', 'RUS', 'ENG'].map(lang => (
                        <div key={lang} className="dropdown-item" onClick={() => setLanguage(lang)}>
                          {lang}
                        </div>
                    ))}
                  </div>
              )}
            </div>
            <div style={{ position: 'relative' }}>
              <HelpCircle size={20} className="header-icon help-icon" onClick={() => setIsHelpOpen(true)} />
              <HelpModal isOpen={isHelpOpen} onClose={() => setIsHelpOpen(false)} />
            </div>

            <div className="user-profile" onClick={() => setIsProfileOpen(!isProfileOpen)} ref={profileDropdownRef}>
              <div className="avatar">
                {userPicture ? (
                    <img src={userPicture} alt={userName} className="avatar-image" />
                ) : (
                    userInitial
                )}
              </div>
              <div className="user-info">
                <span className="user-name">{userName}</span>
                <span className="user-role">{userRoleLabel}</span>
              </div>
              <svg className={`chevron-icon ${isProfileOpen ? 'open' : ''}`} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </div>
          </div>
        </header>

        {/* Main Content Area */}
        <main className="admin-main-content">
          <Outlet />
        </main>

        <AdminProfileFlyout
            isOpen={isProfileOpen}
            onClose={() => setIsProfileOpen(false)}
            userName={userName}
            userInitial={userInitial}
            userPicture={userPicture}
            onPrivacyClick={() => { setIsProfileOpen(false); setIsPrivacyOpen(true); }}
        />
        <PrivacyPolicyDrawer isOpen={isPrivacyOpen} onClose={() => setIsPrivacyOpen(false)} />
        <AddPackageModal isOpen={isAddPackageOpen} onClose={() => setIsAddPackageOpen(false)} onAddSuccess={(data) => { navigate("/admin/packages", { state: { newPackage: data } }); setIsAddPackageOpen(false); }} />
      </div>
  );
}